import type { PlayerLook, RewardBonus } from '@/lib/api/rewards-hub/models';

/** Readable models the leaderboard page consumes (de-obfuscated from `types.ts`). */

/** The tabs of the screen. `all` is the merged board: metrics are summed first, placed after. */
export type LeaderboardSegment = 'all' | 'prediction' | 'crypto';

/**
 * The span the board is counted over (07.10): calendar month and year in UTC,
 * or all time. Volume and Trades count the deals inside it, Best Deal and Best
 * ROE the deals CLOSED inside it (the whole deal, even one opened earlier);
 * Tasks are always all-time. A player with no deals in the period is not on
 * its board. Rank, portrait and glow are the same on every period.
 */
export type LeaderboardPeriod = 'all' | 'day' | 'month' | 'year';

/**
 * A sortable column, and at the same time one of the five category winners —
 * the cards above the table are the head of each column's own ranking.
 */
export type LeaderboardMetric = 'volume' | 'trades' | 'bestDeal' | 'roe' | 'tasks';

/** Which end of the column comes first: the best (`desc`) or the worst (`asc`, 02.11). */
export type LeaderboardSortDirection = 'desc' | 'asc';

/** What the overall ranking is ordered by: a column header picks both. */
export interface LeaderboardSort {
  metric: LeaderboardMetric;
  direction: LeaderboardSortDirection;
}

/** The four counts the rank tooltip breaks a player's standing into. */
export interface LeaderboardArtifacts {
  achievements: number;
  medals: number;
  cups: number;
  diamonds: number;
}

export interface LeaderboardPlayer {
  userId: string;
  /** `null` when the player never set a nickname — draw a placeholder, not an error. */
  username: string | null;
  /** Portrait, frame and nickname glow; `null` — the id-hash picture and a plain name. */
  look: PlayerLook | null;
  /**
   * Rank index, resolved to a name and art through `/scale`. `null` before the
   * onboarding gate: no rank at all, which is not the bottom of the ladder.
   */
  rankIndex: number | null;
}

export interface LeaderboardEntry extends LeaderboardPlayer {
  /** Place in the full ranking under the current column, best first in either direction, 1-based. */
  position: number;
  volume: number;
  trades: number;
  /** Profit of the best closed deal; `null` — no closed deal on this board ("—"). */
  bestDeal: number | null;
  /** Percent, already multiplied out (486 reads "+486%"); `null` like `bestDeal`. */
  roe: number | null;
  tasks: number;
  artifacts: LeaderboardArtifacts;
  /** True on the signed-in player's row. */
  isMe: boolean;
}

export interface LeaderboardWinner {
  metric: LeaderboardMetric;
  player: LeaderboardPlayer;
  /** The winning figure, in the metric's own unit — never `null`: no figure, no card. */
  value: number;
}

export interface RewardsLeaderboard {
  /** What the answer is for — echoed by the server, so a late answer can be told apart. */
  segment: LeaderboardSegment;
  metric: LeaderboardMetric;
  direction: LeaderboardSortDirection;
  period: LeaderboardPeriod;
  /** Leader of each metric, in column order. */
  winners: LeaderboardWinner[];
  /** What the leader of each metric wins; a metric without one plays for no prize. */
  prizes: Partial<Record<LeaderboardMetric, RewardBonus>>;
  /**
   * One page: at most 20 rows, in `direction`. The player's row (marked `isMe`)
   * is in its place on their own page and the 20th on the others — best first
   * only on the pages before it, worst first on every one (02.11). Always print
   * `position`: pinned rows shift the pages.
   */
  entries: LeaderboardEntry[];
  /** The page these rows are, 1-based — echoed, so a late answer can be told apart. */
  page: number;
  /** Pages in the table; 0 on an empty board. */
  pageCount: number;
  /**
   * The player has a place on this board. `false` = no activity here: the
   * table is a plain top 20 and the page prints "—" for them.
   */
  hasStanding: boolean;
}

/** Columns the board can be sorted by, in the order the header draws them. */
export const LEADERBOARD_METRICS: readonly LeaderboardMetric[] = [
  'volume',
  'trades',
  'bestDeal',
  'roe',
  'tasks',
];

/**
 * Tabs in the order the screen draws them: the two products, trading first
 * (29.09). The service still answers `ALL`, the merged board; no tab asks
 * for it any more.
 */
export const LEADERBOARD_SEGMENTS: readonly LeaderboardSegment[] = ['crypto', 'prediction'];

export const DEFAULT_LEADERBOARD_SEGMENT: LeaderboardSegment = 'crypto';

/**
 * The overall ranking goes by completed tasks (Figma 14268:46142, 09.10); a
 * column header still re-sorts it.
 */
export const DEFAULT_LEADERBOARD_METRIC: LeaderboardMetric = 'tasks';

export const LEADERBOARD_SORT_DIRECTIONS: readonly LeaderboardSortDirection[] = ['desc', 'asc'];

export const DEFAULT_LEADERBOARD_DIRECTION: LeaderboardSortDirection = 'desc';

/**
 * The sort a header click leads to (02.11): another column starts best first,
 * the sorted one turns worst first, and a third click takes the sort off.
 */
export function nextLeaderboardSort(
  sort: LeaderboardSort,
  clicked: LeaderboardMetric,
): LeaderboardSort {
  if (clicked !== sort.metric) return { metric: clicked, direction: 'desc' };
  if (sort.direction === 'desc') return { metric: clicked, direction: 'asc' };
  return { metric: DEFAULT_LEADERBOARD_METRIC, direction: DEFAULT_LEADERBOARD_DIRECTION };
}

/** The ranking the page opens on: by Tasks, best first. */
export function isDefaultLeaderboardSort({ metric, direction }: LeaderboardSort): boolean {
  return metric === DEFAULT_LEADERBOARD_METRIC && direction === DEFAULT_LEADERBOARD_DIRECTION;
}

/**
 * Sorted by Best Deal or Best ROE, and nobody on the board has a deal that
 * counts: the column has no leader, and every row would read "—" in id order.
 */
export function hasNoDealToRank({
  metric,
  winners,
}: Pick<RewardsLeaderboard, 'metric' | 'winners'>): boolean {
  const isDealMetric = metric === 'bestDeal' || metric === 'roe';
  return isDealMetric && !winners.some((winner) => winner.metric === metric);
}

/**
 * The period switch, in the order it reads: Today, This month, This year
 * (09.10). It sets the span the category winners — and their prizes — are
 * counted over, and the overall ranking under them under every column (29.10):
 * by Tasks the counts stay all-time, but only the period's players are on it.
 */
export const LEADERBOARD_PERIODS: readonly LeaderboardPeriod[] = ['day', 'month', 'year'];

export const DEFAULT_LEADERBOARD_PERIOD: LeaderboardPeriod = 'month';

/**
 * The periods played for a prize: the month for the monthly one (09.10), the
 * year for the yearly one (Figma 14346:45749). Only there do the gifts and the
 * timer show; Today plays for none.
 */
export type LeaderboardPrizePeriod = Extract<LeaderboardPeriod, 'month' | 'year'>;

export function isPrizePeriod(period: LeaderboardPeriod): period is LeaderboardPrizePeriod {
  return period === 'month' || period === 'year';
}
